export type ExactDecimal = string;

interface DecimalParts {
  sign: -1 | 0 | 1;
  digits: string;
  scale: number;
}

const DECIMAL = /^([+-]?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

function partsOf(value: string): DecimalParts {
  const match = DECIMAL.exec(value);
  if (match === null) throw new Error('invalid exact decimal');
  const negative = match[1] === '-';
  const integer = match[2] ?? '0';
  const fraction = match[3] ?? '';
  const exponent = Number(match[4] ?? '0');
  if (!Number.isSafeInteger(exponent)) throw new Error('invalid exact decimal exponent');

  let digits = `${integer}${fraction}`.replace(/^0+/, '');
  if (digits === '') return { sign: 0, digits: '0', scale: 0 };
  let scale = fraction.length - exponent;
  if (scale < 0) {
    digits += '0'.repeat(-scale);
    scale = 0;
  }
  while (scale > 0 && digits.endsWith('0')) {
    digits = digits.slice(0, -1);
    scale -= 1;
  }
  return { sign: negative ? -1 : 1, digits, scale };
}

function textOf(parts: DecimalParts): ExactDecimal {
  if (parts.sign === 0 || /^0+$/.test(parts.digits)) return '0';
  let digits = parts.digits.replace(/^0+/, '');
  let scale = parts.scale;
  while (scale > 0 && digits.endsWith('0')) {
    digits = digits.slice(0, -1);
    scale -= 1;
  }
  const unsigned =
    scale === 0
      ? digits
      : digits.length > scale
        ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}`
        : `0.${'0'.repeat(scale - digits.length)}${digits}`;
  return parts.sign < 0 ? `-${unsigned}` : unsigned;
}

function signedCoefficient(parts: DecimalParts, scale: number): bigint {
  if (parts.sign === 0) return 0n;
  const coefficient = BigInt(parts.digits) * 10n ** BigInt(scale - parts.scale);
  return parts.sign < 0 ? -coefficient : coefficient;
}

export function exactDecimal(value: string | number): ExactDecimal {
  if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new Error('an exact decimal must be finite');
  }
  return textOf(partsOf(String(value)));
}

export function addExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const a = partsOf(left);
  const b = partsOf(right);
  const scale = Math.max(a.scale, b.scale);
  const total = signedCoefficient(a, scale) + signedCoefficient(b, scale);
  if (total === 0n) return '0';
  return textOf({
    sign: total < 0n ? -1 : 1,
    digits: (total < 0n ? -total : total).toString(),
    scale,
  });
}

export function addExactNumber(value: ExactDecimal, movement: number): ExactDecimal {
  return addExactDecimals(value, exactDecimal(movement));
}

export function subtractExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const parts = partsOf(right);
  const opposite = textOf({ ...parts, sign: parts.sign === 0 ? 0 : parts.sign === 1 ? -1 : 1 });
  return addExactDecimals(left, opposite);
}

export function multiplyExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const a = partsOf(left);
  const b = partsOf(right);
  if (a.sign === 0 || b.sign === 0) return '0';
  return textOf({
    sign: a.sign === b.sign ? 1 : -1,
    digits: (BigInt(a.digits) * BigInt(b.digits)).toString(),
    scale: a.scale + b.scale,
  });
}

export function divideExactDecimals(
  numerator: ExactDecimal,
  denominator: ExactDecimal,
  fractionDigits = 12,
): ExactDecimal {
  const top = partsOf(numerator);
  const bottom = partsOf(denominator);
  if (bottom.sign === 0) throw new Error('cannot divide an exact decimal by zero');
  if (top.sign === 0) return '0';
  if (!Number.isSafeInteger(fractionDigits) || fractionDigits < 0) {
    throw new Error('invalid exact decimal precision');
  }

  let dividend = BigInt(top.digits);
  let divisor = BigInt(bottom.digits);
  const exponent = bottom.scale - top.scale + fractionDigits;
  if (exponent >= 0) dividend *= 10n ** BigInt(exponent);
  else divisor *= 10n ** BigInt(-exponent);

  let quotient = dividend / divisor;
  const remainder = dividend % divisor;
  if (remainder * 2n >= divisor) quotient += 1n;
  return textOf({
    sign: top.sign === bottom.sign ? 1 : -1,
    digits: quotient.toString(),
    scale: fractionDigits,
  });
}

export function midpointExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  return divideExactDecimals(addExactDecimals(left, right), '2');
}

export function compareExactDecimals(left: ExactDecimal, right: ExactDecimal): number {
  const difference = partsOf(subtractExactDecimals(left, right));
  return difference.sign;
}

export function subtractExactToNumber(left: ExactDecimal, right: ExactDecimal): number {
  return Number(subtractExactDecimals(left, right));
}

function naturalLogMagnitude(value: ExactDecimal): number {
  const parts = partsOf(value);
  if (parts.sign === 0) return -Infinity;
  const take = Math.min(15, parts.digits.length);
  const leading = Number(parts.digits.slice(0, take));
  return Math.log(leading) + (parts.digits.length - take - parts.scale) * Math.LN10;
}

export function ratioToExactMagnitude(numerator: number, denominator: ExactDecimal): number {
  if (!Number.isFinite(numerator) || numerator <= 0) {
    throw new Error('an exact-decimal ratio needs a positive finite numerator');
  }
  const logarithm = Math.log(numerator) - naturalLogMagnitude(denominator);
  const ratio = Math.exp(logarithm);
  return ratio === 0 ? Number.MIN_VALUE : ratio;
}

export function projectExactDecimal(
  value: ExactDecimal,
  linearRange: number,
  projectedLimit: number,
): number {
  const parts = partsOf(value);
  if (parts.sign === 0) return 0;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && Math.abs(numeric) <= linearRange) return numeric;

  const beyond = Math.max(0, naturalLogMagnitude(value) - Math.log(linearRange));
  const curve = 1 - Math.exp(-beyond / 8);
  return parts.sign * (linearRange + (projectedLimit - linearRange) * curve);
}
